/**
 * conversation-status — resolve a conversation's handling state to a pill
 * COLOUR (055 FR-013, data-model D-1).
 *
 * The member-facing LABEL is not this module's business: `ConversationStatusPill` renders
 * `status.name` verbatim, exactly as the back office reports it (FR-012). This
 * module decides only how much emphasis that label is painted with.
 *
 * Resolution order, and why it is this order:
 *
 *   1. `closedAt` non-null        → 'closed'
 *   2. `status.id` in the map     → the mapped variant
 *   3. otherwise                  → 'neutral'
 *
 * Closure is evaluated FIRST because `closedAt` is contract-authoritative — the
 * IS contract states outright that replies are refused once it is set and that a
 * consumer MUST key reply-suppression off this field. Ordering it first means
 * the grey "closed" pill works TODAY, so the blocked parameter below withholds
 * only the received/in-progress distinction rather than all emphasis.
 *
 * ── BP-001: STATUS_ID_VARIANTS IS DELIBERATELY EMPTY ────────────────────────
 *
 * `CaseStatusResponse` is a free-form `{ id?: integer, name?: string }` with NO
 * enumerated values — the integration contract defines no state vocabulary at
 * all, and no authoritative list exists in this project. The only status values
 * anywhere in the repo are invented test fixtures, which carry no authority.
 *
 * So the map ships empty, and every open conversation renders neutral.
 *
 * DO NOT populate it with plausible-looking guesses. A pill painted "in
 * progress" against a state the fund never said meant that is a fabricated
 * assertion about a member's case — the precise shape Constitution P VII
 * forbids, and invisible to us because it looks right.
 *
 * Unblock: YTK confirms the authoritative conversation-state identifier list →
 * record it in `docs/governance/decisions/` → populate this map in the same
 * change → update `__tests__/conversation-status.test.ts`, whose "is EMPTY"
 * assertion exists to make that a deliberate act rather than a drive-by edit.
 */

import type { CaseStatusView } from './CasesReaderPort.js';

/** The emphasis a status label is painted with. `neutral` asserts nothing. */
export type ConversationStatusVariant = 'received' | 'progress' | 'closed' | 'neutral';

/** The back-office handling state, as the read projects it — the port's view type. */
export type ConversationStatus = CaseStatusView;

/**
 * Authorised back-office state id → pill variant.
 *
 * EMPTY BY DESIGN — see the BP-001 note above. This is a blocked parameter, not
 * an unfinished lookup table.
 */
export const STATUS_ID_VARIANTS: ReadonlyMap<number, ConversationStatusVariant> = new Map();

/**
 * Resolve the pill variant for one conversation.
 *
 * `closedAt` accepts `null | undefined` because the contract treats both as
 * "open or unknown" — and is explicit that a consumer must NOT infer "closed"
 * from the absence of a value.
 *
 * `variants` is injectable for tests only; production callers omit it and get
 * the (empty) authorised map.
 */
export function resolveConversationStatusVariant(
  status: ConversationStatus,
  closedAt: string | null | undefined,
  variants: ReadonlyMap<number, ConversationStatusVariant> = STATUS_ID_VARIANTS,
): ConversationStatusVariant {
  if (closedAt != null) {
    return 'closed';
  }
  if (status.id !== undefined) {
    return variants.get(status.id) ?? 'neutral';
  }
  return 'neutral';
}

/**
 * The label to show, or `null` when the back office supplied nothing usable
 * (FR-012: show no pill at all rather than a fabricated or code-shaped label).
 *
 * Falls back to the id as a STRING only because an id is a real value the back
 * office did send — it is opaque to the member, but it is not invented.
 */
export function conversationStatusLabel(status: ConversationStatus): string | null {
  if (status.name !== undefined && status.name.trim().length > 0) {
    return status.name;
  }
  return status.id !== undefined ? String(status.id) : null;
}
